import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Search,
  ArrowRight,
  Download,
  Loader2,
  User,
  Users,
  Clock,
  CreditCard,
  UserPlus,
  Tag,
  CalendarDays,
  CircleDollarSign,
  Phone,
  Mail,
} from "lucide-react";
import CommonGrid from "@/components/pos/admin/common/grid";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import { useDebounce } from "@/hooks/pos/use-debounce";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { AdminWaiver, AdminWaiversResponse, GuestType } from "@/constants";
import { posApiFetch } from "@/utils/pos-api";
import { useToast } from "@/hooks/use-toast";
import { downloadBlob } from "@/utils/download";
import { formatDate, formatShortCryptoAddress, formatMoneyTotal } from "@/utils/format";
import useSWR from "swr";

// --- Helpers ---

const formatGuestType = (guestType: GuestType | null | undefined): string => {
  if (!guestType) return "N/A";
  const labels: Record<GuestType, string> = { couples: "Couples", single_ladies: "Single Ladies", single_males: "Single Males" };
  return labels[guestType] || guestType;
};

const formatDuration = (months: number | null | undefined): string => {
  if (!months) return "N/A";
  if (months >= 12) {
    const years = months / 12;
    return years === 1 ? "1 Year" : `${years} Years`;
  }
  return months === 1 ? "1 Month" : `${months} Months`;
};

// Each row in the table is a grouped membership (may have multiple waivers)
interface WaiverRow {
  membershipId: number;
  fullName: string;
  emailAddress: string;
  phoneNumber: string;
  customerAddress: string;
  profileImageUrl?: string;
  membership: any;
  waivers: Record<string, AdminWaiver>;
  status: string;
}

type WaiversState = {
  searchQuery?: string;
  currentPage?: number;
  itemsPerPage?: number;
};

const defaultState: WaiversState = {
  searchQuery: "",
  currentPage: 1,
  itemsPerPage: 10,
};

const WaiversPage: React.FC = () => {
  const { toast } = useToast();

  useEffect(() => {
    document.title = "Waivers | SECH Admin";
  }, []);

  const [pageState, setPageState] = usePagePersistentState<WaiversState>("waivers", defaultState);
  const searchQuery = pageState.searchQuery;
  const currentPage = pageState.currentPage;
  const itemsPerPage = pageState.itemsPerPage;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "waivers_table",
    defaultColumn: "fullName",
    defaultDirection: "asc",
  });

  const [selectedWaiver, setSelectedWaiver] = useState<WaiverRow | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<string | null>(null);

  const { data: waiversResponse, error } = useSWR<AdminWaiversResponse>(
    `/v1/pos/admin/waivers?pageSize=500`,
    async () => {
      const response = await posApiFetch("/v1/pos/admin/waivers?pageSize=500");
      return response.json();
    }
  );

  const isLoading = !waiversResponse && !error;
  const waivers = waiversResponse?.data ?? [];

  // Group waivers by membershipId into rows
  const waiverRows: WaiverRow[] = useMemo(() => {
    const grouped: Record<number, WaiverRow> = {};
    for (const waiver of waivers) {
      const mid = waiver.membershipId;
      if (!grouped[mid]) {
        grouped[mid] = {
          membershipId: mid,
          fullName: waiver.fullName,
          emailAddress: waiver.emailAddress,
          phoneNumber: waiver.phoneNumber,
          customerAddress: waiver.customerAddress ?? "",
          profileImageUrl: waiver.profileImageUrl,
          membership: waiver.membership,
          waivers: {},
          status: waiver.membership?.status ?? "unknown",
        };
      }
      grouped[mid].waivers[waiver.type] = waiver;
    }
    return Object.values(grouped);
  }, [waivers]);

  const downloadWaiverPdf = async (waiver: AdminWaiver) => {
    setIsDownloadingPdf(waiver.type);
    try {
      // Use the POS-namespaced PDF route directly — `waiver.pdfUri` points at
      // the Firebase-gated /memberships/:id/waiver/:type endpoint and isn't
      // reachable from a POS-cookie session.
      const fullUrl = `/v1/pos/admin/waivers/${waiver.membershipId}/pdf/${waiver.type}`;
      const response = await posApiFetch(fullUrl, { method: "GET" });
      const pdfBlob = await response.blob();
      downloadBlob(pdfBlob, `waiver-${waiver.type}-${waiver.id}.pdf`);
    } catch (err: any) {
      toast({ title: "PDF Generation Failed", description: err.message, variant: "destructive" });
    }
    setIsDownloadingPdf(null);
  };

  const handleViewDetails = (row: WaiverRow) => {
    setSelectedWaiver(row);
    setIsDetailOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active": return "bg-green-100 text-green-800";
      case "expired": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  const getStatusDotColor = (status: string) => {
    switch (status) {
      case "active": return "bg-green-800";
      case "expired": return "bg-red-800";
      default: return "bg-gray-500";
    }
  };

  const tableColumns = useMemo(() => [
    {
      label: "Name", field: "fullName", sortable: true,
      cellTemplate: (value: string, row: WaiverRow) => (
        <div className="flex items-center gap-2">
          {row.profileImageUrl ? (
            <img src={row.profileImageUrl} alt={value} className="h-7 w-7 rounded-full object-cover shrink-0" />
          ) : (
            <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
              <User className="h-3.5 w-3.5 text-gray-500" />
            </div>
          )}
          <span className="text-[13px] text-[var(--secondary-text-color)] font-medium">{value}</span>
        </div>
      ),
    },
    {
      label: "Email", field: "emailAddress", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{value}</span>
      ),
    },
    {
      label: "Plan", field: "membership",
      cellTemplate: (value: any) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{value?.templateName || value?.type || "N/A"}</span>
      ),
    },
    {
      label: "Guest Type", field: "membership",
      cellTemplate: (value: any) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{formatGuestType(value?.guestType)}</span>
      ),
    },
    {
      label: "Status", field: "status", sortable: true,
      cellTemplate: (value: string) => (
        <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${getStatusColor(value)}`}>
          <span className={`h-2 w-2 rounded-full mr-1 ${getStatusDotColor(value)} inline-flex items-center justify-center`} />
          <span className="leading-5 text-[13px] font-normal">{value.charAt(0).toUpperCase() + value.slice(1)}</span>
        </span>
      ),
    },
    {
      label: "Waivers", field: "waivers",
      cellTemplate: (value: Record<string, AdminWaiver>) => {
        const count = Object.keys(value).length;
        return <span className="text-[13px] text-[var(--secondary-text-color)]">{count} signed</span>;
      },
    },
    {
      label: "Action", field: "membershipId",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: number, row: WaiverRow) => (
        <span
          className="group text-[13px] hover:text-[#CC1417] text-[var(--primary-color)] flex items-center gap-2 cursor-pointer"
          onClick={() => handleViewDetails(row)}
        >
          Details
          <ArrowRight className="transform transition-transform duration-300 group-hover:translate-x-1" width={16} height={16} />
        </span>
      ),
    },
  ], []);

  const filteredRows = useMemo(() => {
    const search = debouncedSearch.toLowerCase();
    if (!search) return waiverRows;
    return waiverRows.filter((row) =>
      row.fullName?.toLowerCase().includes(search)
      || row.emailAddress?.toLowerCase().includes(search)
      || row.phoneNumber?.toLowerCase().includes(search)
      || row.customerAddress?.toLowerCase().includes(search)
      || row.membership?.templateName?.toLowerCase().includes(search)
    );
  }, [waiverRows, debouncedSearch]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "fullName": aVal = a.fullName ?? ""; bVal = b.fullName ?? ""; break;
        case "emailAddress": aVal = a.emailAddress ?? ""; bVal = b.emailAddress ?? ""; break;
        case "status": aVal = a.status ?? ""; bVal = b.status ?? ""; break;
        default: aVal = a.fullName ?? ""; bVal = b.fullName ?? "";
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredRows, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedRows.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedRows = sortedRows.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setPageState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setPageState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedRows.length, handlePageChange, handleItemsPerPageChange };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Waivers</h2>
          <p className="text-[var(--secondary-text-color)] mb-4">Failed to load waivers</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">
          Waivers
          {waiversResponse?.meta?.total !== undefined && (
            <span className="text-[13px] font-normal text-[var(--secondary-text-color)] ml-2">
              ({waiversResponse.meta.total} total)
            </span>
          )}
        </p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input
              className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center"
              type="text"
              placeholder="Search by name, email, phone, or wallet"
              value={searchQuery ?? ""}
              onChange={(e) => setPageState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))}
            />
          </div>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="pt-3 h-[calc(100vh-130px)]">
          {!paginatedRows.length && !isLoading ? (
            <NoRecordFound title="Waivers" description="There are no waivers matching your search." />
          ) : (
            <CommonGrid
              rowData={paginatedRows}
              isLoading={isLoading}
              columns={tableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>

      {/* Waiver Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={(open) => { setIsDetailOpen(open); if (!open) setSelectedWaiver(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[16px] font-semibold">Waiver Details</DialogTitle>
          </DialogHeader>

          {selectedWaiver && (() => {
            const row = selectedWaiver;
            const membership = row.membership;
            return (
              <div className="space-y-4">
                {/* Profile header */}
                <div className="flex items-center gap-3">
                  {row.profileImageUrl ? (
                    <img src={row.profileImageUrl} alt={row.fullName} className="h-14 w-14 rounded-full object-cover ring-2 ring-gray-200 shrink-0" />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center ring-2 ring-gray-200 shrink-0">
                      <User className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <h3 className="text-[16px] font-bold text-[var(--primary-text-color)]">{row.fullName}</h3>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${membership?.status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {membership?.status === "active" ? "Active" : membership?.status === "expired" ? "Expired" : "Inactive"}
                      </span>
                    </div>
                    <span className="text-[12px] text-[var(--secondary-text-color)]">
                      Membership #{membership?.id}
                      {membership?.transactionId && <> &middot; Transaction #{membership.transactionId}</>}
                    </span>
                  </div>
                </div>

                {/* Membership details grid */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-[13px] border-t border-gray-200 pt-3">
                  <div className="flex items-center gap-2">
                    <Tag className="h-3.5 w-3.5 text-[var(--secondary-text-color)] shrink-0" />
                    <span className="text-[var(--secondary-text-color)]">Plan:</span>
                    <span className="font-medium text-[var(--primary-text-color)]">{membership?.templateName || membership?.type || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-3.5 w-3.5 text-[var(--secondary-text-color)] shrink-0" />
                    <span className="text-[var(--secondary-text-color)]">Guest Type:</span>
                    <span className="font-medium text-[var(--primary-text-color)]">{formatGuestType(membership?.guestType)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-[var(--secondary-text-color)] shrink-0" />
                    <span className="text-[var(--secondary-text-color)]">Duration:</span>
                    <span className="font-medium text-[var(--primary-text-color)]">{formatDuration(membership?.durationMonths)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-3.5 w-3.5 text-[var(--secondary-text-color)] shrink-0" />
                    <span className="text-[var(--secondary-text-color)]">Price:</span>
                    <span className="font-medium text-[var(--primary-text-color)]">{membership?.price ? `$${formatMoneyTotal(membership.price)}` : "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5 text-[var(--secondary-text-color)] shrink-0" />
                    <span className="text-[var(--secondary-text-color)]">Primary:</span>
                    <span className="font-medium text-[var(--primary-text-color)]">{membership?.primaryName || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <UserPlus className="h-3.5 w-3.5 text-[var(--secondary-text-color)] shrink-0" />
                    <span className="text-[var(--secondary-text-color)]">Partner:</span>
                    <span className="font-medium text-[var(--primary-text-color)]">{membership?.partnerName || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-3.5 w-3.5 text-[var(--secondary-text-color)] shrink-0" />
                    <span className="text-[var(--secondary-text-color)]">Period:</span>
                    <span className="font-medium text-[var(--primary-text-color)]">{formatDate(membership?.startDate)} - {formatDate(membership?.endDate)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CircleDollarSign className="h-3.5 w-3.5 text-[var(--secondary-text-color)] shrink-0" />
                    <span className="text-[var(--secondary-text-color)]">Wallet:</span>
                    <span className="font-medium text-[var(--primary-text-color)] font-mono text-[12px]">{formatShortCryptoAddress(row.customerAddress)}</span>
                  </div>
                </div>

                {/* Contact info */}
                <div className="flex flex-wrap gap-4 text-[13px] border-t border-gray-200 pt-3">
                  {row.phoneNumber && (
                    <a href={`tel:${row.phoneNumber}`} className="flex items-center gap-1.5 text-[var(--link-color)] hover:underline">
                      <Phone className="h-3.5 w-3.5" />
                      {row.phoneNumber}
                    </a>
                  )}
                  {row.emailAddress && (
                    <a href={`mailto:${row.emailAddress}`} className="flex items-center gap-1.5 text-[var(--link-color)] hover:underline">
                      <Mail className="h-3.5 w-3.5" />
                      {row.emailAddress}
                    </a>
                  )}
                </div>

                {/* Download buttons */}
                <div className="flex gap-2 border-t border-gray-200 pt-3">
                  {row.waivers.club_swinkster && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadWaiverPdf(row.waivers.club_swinkster)}
                      disabled={isDownloadingPdf === "club_swinkster"}
                      className="text-[13px]"
                    >
                      {isDownloadingPdf === "club_swinkster" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                      Club Swinkster
                    </Button>
                  )}
                  {row.waivers.secrets_hideaway && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadWaiverPdf(row.waivers.secrets_hideaway)}
                      disabled={isDownloadingPdf === "secrets_hideaway"}
                      className="text-[13px]"
                    >
                      {isDownloadingPdf === "secrets_hideaway" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                      Secrets Hideaway
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WaiversPage;
